import { alpha, Box, Stack } from "@mui/material";
import {
  DataGridProps,
  gridClasses as dataGridClasses,
  GridColDef,
  GridRowSelectionModel,
  GridActionsColDef,
  GridCallbackDetails,
} from "@mui/x-data-grid";

export const SMALL_ITEMS_PER_PAGE = 5;
export const ITEMS_PER_PAGE = 10;

export const customDataGridClasses = {
  rowClickable: "MuiDataGrid-row-clickable",
  rowDisabled: "MuiDataGrid-row-disabled",
  none: "",
};

export const getCommonDataGridProps = <T extends WithId>(
  onRowClick?: ICommonDataGridProps<T>["onRowClick"],
  disabledRowIds?: ICommonDataGridProps<T>["disabledRowIds"],
  isRowDisabled?: (row: T) => boolean,
  hidePageSizeText?: ICommonDataGridProps<T>["hidePageSizeText"]
): Partial<DataGridProps> => {
  const getRowClassName = (row: TWithCommonId<T>): string => {
    if (Boolean(onRowClick)) {
      if (disabledRowIds?.includes(row.id) || isRowDisabled?.(row)) {
        return customDataGridClasses.rowDisabled;
      }

      return customDataGridClasses.rowClickable;
    }

    return customDataGridClasses.none;
  };

  return {
    localeText: {
      paginationRowsPerPage: hidePageSizeText ? "" : "Počet riadkov na stránku",
      paginationDisplayedRows({ count, from, to }) {
        return `${from}-${to} z ${count}`;
      },
      footerTotalRows: "z",
      columnHeaderSortIconLabel: "Zoradiť",
      noResultsOverlayLabel: "Nenašli sa žiadne výsledky",
      noRowsLabel: "Nenašli sa žiadne výsledky",
    },
    sx: (theme) => ({
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      ".MuiDataGrid-withBorderColor": {
        borderColor: theme.palette.grey[200],
      },
      "--DataGrid-rowBorderColor": theme.palette.grey[200],
      ".MuiDataGrid-container--top [role=row]": {
        background: alpha(theme.palette.grey[100], 0.5),
      },
      ".MuiDataGrid-columnSeparator": {
        height: "100%",
        "&:hover, &.MuiDataGrid-columnSeparator--resizing": {
          ".MuiBox-root": {
            backgroundColor: alpha(theme.palette.primary.main, 0.4),
            width: "5px",
          },
        },
      },
      ".MuiDataGrid-columnHeader:hover": {
        ".MuiDataGrid-iconButtonContainer": {
          marginLeft: 0.5,
        },
      },
      ".MuiDataGrid-columnHeader--siblingFocused .MuiDataGrid-columnSeparator, .MuiDataGrid-columnHeader:focus .MuiDataGrid-columnSeparator":
        {
          opacity: 1,
        },
      ".MuiDataGrid-actionsCell": { gridGap: 4 },
      [`& .${dataGridClasses.columnHeader}, & .${dataGridClasses.cell}`]: {
        outline: "transparent",
        fontSize: 13,
        fontWeight: 500,
      },
      [`& .${dataGridClasses.columnHeader}`]: {
        backgroundColor: "#567287",
        color: "#fff !important",
        "& *": {
          color: "#fff !important",
        },
      },
      [`& .${dataGridClasses.footerContainer}`]: {
        backgroundColor: "#567287",
      },
      "& .MuiTablePagination-root": {
        color: "#fff !important",

        "& .MuiSvgIcon-root": {
          color: "#fff !important",
        },
      },
      [`& .${dataGridClasses.columnHeader}:focus-within, & .${dataGridClasses.cell}:focus-within`]:
        {
          outline: "none",
        },
      [`& .${customDataGridClasses.rowClickable}`]: {
        cursor: "pointer",
      },
      [`& .${customDataGridClasses.rowDisabled}`]: {
        opacity: 0.6,
        cursor: "not-allowed",
      },
    }),
    slots: {
      columnResizeIcon: () => (
        <Stack height="100%" alignItems="center" width={7}>
          <Stack
            alignItems="center"
            justifyContent="center"
            height="100%"
            width="1px"
            sx={(theme) => ({
              backgroundColor: theme.palette.divider,
            })}
          >
            <Box
              width="1px"
              height="75%"
              sx={(theme) => ({
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.divider,
                transition: theme.transitions.create(
                  ["background-color", "width"],
                  { duration: 200 }
                ),
              })}
            />
          </Stack>
        </Stack>
      ),
    },
    onRowClick: ({ row }) =>
      disabledRowIds?.includes(row.id) || isRowDisabled?.(row)
        ? null
        : onRowClick?.(row),
    getRowClassName: Boolean(onRowClick)
      ? ({ row }) => getRowClassName(row as TWithCommonId<T>)
      : undefined,
  };
};

type WithCommonId = { id: string };
export type WithId = WithCommonId | { _id: string };
export type TWithId<T> = WithId & T;
export type TWithCommonId<T> = WithCommonId & T;

type TColumnAdditionalProps = {};

export interface ICommonDataGridProps<T extends WithId> {
  id?: string;
  columns: (GridColDef<T> & TColumnAdditionalProps)[];
  rows: TWithId<T>[];
  onPageSizeChange?: (size: number) => void;
  onPageChange?: (page: number) => void;
  onSelectedRowsChange?: (
    selectedRows: GridRowSelectionModel,
    detail: GridCallbackDetails
  ) => void;
  rowSelectionModel?: GridRowSelectionModel;
  pageSizeOptions?: number[];
  rowHeight?: number;
  getRowHeight?: DataGridProps["getRowHeight"];
  onRowClick?: (row: T) => void;
  disabledRowIds?: string[];
  sx?: DataGridProps["sx"];
  pageSizeInitial?: number;
  editMode?: DataGridProps["editMode"];
  rowModesModel?: DataGridProps["rowModesModel"];
  onRowModesModelChange?: DataGridProps["onRowModesModelChange"];
  onRowEditStop?: DataGridProps["onRowEditStop"];
  processRowUpdate?: DataGridProps["processRowUpdate"];
  hidePageSizeText?: boolean;
}

export const mapRowsToCommon = <T,>(rows: TWithId<T>[]): TWithCommonId<T>[] => {
  return rows.map((row) => {
    if ("id" in row) {
      return row;
    }

    return { ...row, id: row._id };
  });
};

export const mapColumnsToCommon = <T extends WithId>(
  columns: (GridColDef<T> | (GridActionsColDef<T> & TColumnAdditionalProps))[]
): GridColDef<T>[] => {
  return columns.map((column) => {
    return {
      ...column,
      sortable: column.sortable ?? false,
      disableColumnMenu: true,
    };
  });
};
